import { Alert, Group, Text } from '@mantine/core'
import type { ReactNode } from 'react'

import { IconCloudOff, IconError, IconInfo, IconWarning } from './icons'

export type BannerTone = 'info' | 'warning' | 'error' | 'offline'

const TONES: Record<BannerTone, { color: string; icon: ReactNode }> = {
  info: { color: 'teal', icon: <IconInfo size={20} /> },
  warning: { color: 'yellow', icon: <IconWarning size={20} /> },
  error: { color: 'red', icon: <IconError size={20} /> },
  offline: { color: 'gray', icon: <IconCloudOff size={20} /> },
}

/**
 * A screen-level message (UX spec §4.5): offline, a failed load, setup to finish, a check-in due. Icon, one line and
 * an optional action; [title] and [children] for the few that need more. Replaces ad hoc Alerts and red paragraphs.
 * The action is a `size="compact-sm" variant="light"` Button or ButtonLink, so banners read alike everywhere.
 */
export function StatusBanner(props: {
  tone: BannerTone
  children: ReactNode
  title?: string
  action?: ReactNode
  onClose?: () => void
}) {
  const tone = TONES[props.tone]
  return (
    <Alert
      variant="light"
      color={tone.color}
      icon={tone.icon}
      title={props.title}
      withCloseButton={props.onClose !== undefined}
      closeButtonLabel="Dismiss"
      onClose={props.onClose}
      role={props.tone === 'error' ? 'alert' : 'status'}
      radius="md"
    >
      <Group justify="space-between" gap="sm" wrap="wrap" align="center">
        <Text size="sm" component="div" style={{ flex: '1 1 240px' }}>
          {props.children}
        </Text>
        {props.action}
      </Group>
    </Alert>
  )
}
